import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AppBar, Tab, Tabs, Typography, IconButton, styled, keyframes } from '@mui/material';
import { Routes } from 'router';
import { ReactComponent as Logo } from 'media/byonsides_logo.svg';
import { useAuth } from 'context/AuthContext';
import LogoutIcon from '@mui/icons-material/Logout';
import { colors } from '../theme/colors';

// Pulse animation for logo
const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
`;

// Glass morphism AppBar
const GlassAppBar = styled(AppBar)({
  background: 'rgba(255, 255, 255, 0.8)',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  boxShadow: 'none',
  borderBottom: `1px solid ${colors.neutral[200]}`,
  color: colors.text.primary,
});

// Logo container with pulse on hover
const LogoContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  '& svg': {
    transition: 'all 0.3s ease-in-out',
    fill: colors.primary.main,
  },
  '&:hover svg': {
    animation: `${pulse} 1s ease-in-out`,
  },
});

// Custom styled tabs
const StyledTabs = styled(Tabs)({
  minHeight: 48,
  '& .MuiTabs-indicator': {
    height: 3,
    borderRadius: '3px 3px 0 0',
    background: colors.primary.gradient,
    transition: 'all 0.3s ease-in-out',
  },
});

// Custom styled tab
const StyledTab = styled(Tab)({
  minHeight: 48,
  textTransform: 'none',
  fontSize: '0.875rem',
  fontWeight: 500,
  color: colors.text.secondary,
  transition: 'all 0.2s ease-in-out',
  position: 'relative',
  '&:hover': {
    color: colors.primary.main,
    background: 'rgba(8, 131, 149, 0.05)',
  },
  '&.Mui-selected': {
    fontWeight: 600,
    color: colors.primary.main,
  },
});

const Header: React.FC = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [scrollY, setScrollY] = useState(0);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  const currentPath = location.pathname;

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollDifference = currentScrollY - lastScrollY;

      setScrollY(prev => Math.min(Math.max(prev + scrollDifference, 0), 84));

      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [lastScrollY]);

  const handleMouseEnter = () => {
    setIsHovered(true);
    setScrollY(0);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  return (
    <>
      <GlassAppBar
        position="fixed"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{
          transition: 'transform 0.3s ease',
          transform: `translateY(-${isHovered ? 0 : scrollY}px)`,
          zIndex: 1000,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '12px 24px',
            justifyContent: 'space-between',
          }}
        >
          <Link to={Routes.Home} style={{ textDecoration: 'none' }}>
            <LogoContainer>
            <Logo style={{ height: '40px', width: '40px', marginRight: '12px' }} />
            <Typography
              variant="h5"
              sx={{
                fontFamily: '"DM Sans", "Urbanist", sans-serif',
                fontWeight: 600,
                color: colors.text.primary,
                '& b': {
                  background: colors.primary.gradient,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                },
              }}
            >
              byonsides <b>Aqua</b>
            </Typography>
          </LogoContainer>
          </Link>
          {user && (
            <IconButton
              onClick={logout}
              sx={{
                color: colors.text.secondary,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  color: colors.error.main,
                  transform: 'scale(1.1)',
                  backgroundColor: 'rgba(255, 107, 107, 0.1)',
                },
              }}
            >
              <LogoutIcon />
            </IconButton>
          )}
        </div>
        {user && (
          <StyledTabs value={currentPath} variant="fullWidth">
            <StyledTab
              label="Управление устройствами"
              value={Routes.DevicesListManagement}
              component={Link}
              to={Routes.DevicesListManagement}
            />
            <StyledTab
              label="Семейства устройств"
              value={Routes.DevicesFamiliesManagement}
              component={Link}
              to={Routes.DevicesFamiliesManagement}
            />
            <StyledTab
              label="Контракторы"
              value={Routes.Contractors}
              component={Link}
              to={Routes.Contractors}
            />
          </StyledTabs>
        )}
      </GlassAppBar>
      <div style={{ paddingTop: user ? '120px' : '80px' }}>
        {/* Main content goes here */}
      </div>
    </>
  );
};

export default Header;